<template>
  <div
    :class="{
      active: isActive,
      submenu: submenu,
      closable: (isCached || isActive) && closable,
      cached: isCached,
      disabled: disabled,
      detailed: hasDetails,
    }"
    class="kbo-sidebar--item"
  >
    <div class="button" :style="buttonStyle" @click="handleClick">
      <slot>
        <slot name="icon">
          <div v-if="icon && iconType === 'font-awesome'" :class="`icon ${icon}`"></div>
          <div v-if="icon && iconType === 'google'" class="material-icons icon">{{ icon }}</div>
          <div v-if="img">
            <img :alt="label" :src="img" class="img-icon" height="24px" />
          </div>
        </slot>
        <div class="label-container">
          <template v-if="badge">
            <el-badge type="info" class="item" :value="badge">
              <div :style="labelStyle" class="label">{{ label }}</div>
            </el-badge>
          </template>
          <template v-else>
            <div :style="labelStyle" class="label">{{ label }}</div>
          </template>
          <div v-if="hasDetails" class="details">{{ details }}</div>
        </div>
      </slot>
    </div>
    <div class="close" @click.capture="handleClose">
      <div class="close-icon">
        <i class="fas fa-times"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EventBus from "@/events/eventbus";

export default {
  name: "KboSidebarItem",
  props: {
    route: String,
    icon: String,
    iconType: {
      type: String,
      default: "font-awesome",
    },
    img: String,
    exact: Boolean,
    label: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    details: String,
    active: String,
    submenu: Boolean,
    badge: String,
    closable: {
      type: Boolean,
      default: true,
    },
    buttonStyle: Object,
    labelStyle: Object,
  },
  data() {
    return {
      isActive: false,
      isCached: false,
    };
  },
  computed: {
    ...mapGetters("cache", ["cachedRoutes"]),
    ...mapGetters("persistedCache", ["persistedCachedRoutes"]),
    hasDetails() {
      return this.details && this.details !== "";
    },
  },
  watch: {
    "$route.fullPath"() {
      this.computeIsCached();
      this.computeIsActive();
    },
  },
  created() {
    EventBus.$on("persisted-cached-routes-updated", this.computeIsCached);
    this.computeIsCached();
    this.computeIsActive();
  },
  beforeDestroy() {
    EventBus.$off("persisted-cached-routes-updated", this.computeIsCached);
  },
  methods: {
    ...mapActions("cache", ["removeCachedRoute"]),
    ...mapActions("persistedCache", ["removePersistedCachedRoute"]),
    computeIsActive() {
      if (this.active) {
        this.isActive = this.$route.fullPath.match(this.active);
      } else if (this.route) {
        if (this.exact) {
          this.isActive = this.$route.fullPath === this.route;
        } else {
          this.isActive = this.$route.fullPath.match(this.route);
        }
      } else {
        this.isActive = false;
      }
    },
    computeIsCached() {
      let route;
      if (this.active && typeof this.active !== "function") {
        route = this.active.replaceAll("/", "\\/");
      } else if (this.route) {
        route = this.route.replaceAll("/", "\\/");
      }
      if (route) {
        const re = new RegExp(route);
        this.isCached = this.isRouteCached(re) === true || this.isRoutePersistedCached(re) === true;
      } else {
        this.isCached = false;
      }
    },
    isRouteCached(regex) {
      return !!Object.keys(this.cachedRoutes).find((url) => url.match(regex));
    },
    isRoutePersistedCached(regex) {
      return !!Object.keys(this.persistedCachedRoutes).find((url) => url.match(regex));
    },
    handleClick() {
      if (this.disabled) return;
      this.$emit("click");
      const el = document.getElementById("kbo-sidebar");
      if (el) {
        el.classList.add("close");
        setTimeout(() => {
          if (el) {
            el.classList.remove("close");
          }
        }, 100);
      }
      if (this.route && this.$route.path !== this.route) {
        this.$router.push(this.route);
      }
    },
    handleClose() {
      let timeout = 1;
      if (this.$route.fullPath === this.route) {
        this.$router.push(this.$route.query.fromName ? { name: this.$route.query.fromName } : "/");
        timeout = 500;
      }
      setTimeout(() => {
        this.removeCachedRoute(this.route);
        this.removePersistedCachedRoute(this.route);
      }, timeout);
    },
  },
};
</script>

<style lang="scss" scoped>
.kbo-sidebar--item {
  cursor: pointer;
  background-color: $menuBg2;
  position: relative;
  .label-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  &.disabled {
    .button {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .button {
    padding: 8px 0;
    width: 54px;
    height: 48px;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
  }

  .close {
    position: absolute;
    right: -48px;
    border-left: 2px solid white;
    text-align: center;
    width: 48px;
    height: 48px;
    justify-content: center;
    top: 0px;
    display: none;
    align-items: center;

    div.close-icon {
      width: 100%;
      height: 100%;
      color: white;
      background-color: rgb(179, 85, 85);
      border-radius: 0;
      display: flex;
      justify-content: center;
      align-items: center;

      &:hover {
        background-color: rgb(187, 29, 29);
      }

      i {
        font-size: 24px;
      }
    }
  }

  &.active {
    background: $menuActive !important;
  }

  &.cached {
    position: relative;

    &:before {
      display: block;
      position: absolute;
      top: 3px;
      left: 3px;
      height: 6px;
      width: 6px;
      background-color: white;
      content: "";
      border-radius: 3px;
    }
  }

  &.closable:hover .close {
    display: flex;
  }

  &.submenu {
    height: 40px;
    min-width: 180px;

    &.cached {
      .close {
        height: 40px;
        width: 40px;
        right: -40px;
      }
    }

    .button {
      flex-direction: row;
      justify-content: flex-start;
      padding: 0 12px 0 6px;
      height: 40px;
      width: 100%;
    }

    &.detailed {
      .label {
        font-size: 9px;
      }

      .details {
        font-size: 13px;
        font-weight: bold;
      }
    }

    .label-container {
      padding-left: 2px;
    }

    .label {
      font-size: 14px;
      white-space: nowrap;
    }

    .icon {
      font-size: 20px;
      width: 32px;
      padding-right: 6px;
      text-align: center;
      margin-bottom: 0;

      &.material-icons {
        font-size: 24px;
      }
    }

    .img-icon {
      height: auto;
      padding-right: 6px;
      width: 32px;
    }
  }

  &:not(.disabled):hover {
    background-color: $menuHover;
    color: white;
  }

  .icon {
    font-size: 20px;
    color: white;
    margin-bottom: 2px;
  }

  .img-icon {
    height: 22px;
  }

  .label {
    text-align: center;
    font-size: 9px;
    line-height: 8px;
  }
}

::v-deep .el-badge__content {
  right: 3px;
  top: -2px;
  height: 14px;
  padding: 0 5px;
  background-color: black;
  border: none;
  font-size: 9px;
  font-weight: bold;
  line-height: 14px;
}

.neositek {
  .kbo-sidebar--item {
    background-color: $neositekMenuBg;

    &.active {
      background-color: $neositekMenuActiveBg !important;
    }

    &:hover {
      background-color: $neositekMenuActiveBg !important;
    }
  }
}
</style>
